/**
 * Shared failure-detection for apiService responses (the ONE idiom for every
 * saga's error branch — see the #603 gate legs' class enumeration).
 *
 * The fetchers NEVER reject: every failure resolves to a plain object that
 * carries a status code, and a 200/201 resolves the parsed body. The emit
 * shapes (src/services/apiService.js), which this predicate must classify:
 *
 *   - doGetCall (:5)      non-200        → { status: <number>, errMsg }
 *                         network reject → { status: 500, errMsg }            (numeric)
 *   - doUpdateCall (:30)  non-2xx        → { errStatus: <number>, errMsg }
 *                         network reject → { errStatus: "500", errMsg }       (STRING "500")
 *   - doDeleteCall (:63)  non-200        → { errStatus: <number>, errMsg }
 *                         network reject → { errStatus: "500", errMsg }       (STRING "500")
 *   - inline POST/PUT fetchers getQuestionMetadata (:357), reviewQuestion (:392),
 *     getDemoQuestionMetadata (:563) → { status: <number>, errMsg } on non-200
 *                                       and on network reject (numeric, #600)
 *   - powerSearch → { errStatus, ... } on reject
 *
 * THIS predicate is for the GET family (doGetCall + the inline fetchers that
 * emit the {status} shape): there a truthy `errStatus || status` is exactly the
 * failure signal — the historical guard at getAbsiteDiagnosticReport
 * (quiz.js:82) generalized — because every traced GET success body is an array
 * or a DTO with no top-level `status`. Both numeric (500) and string ("500")
 * shapes are handled identically (both truthy). Update/delete-backed sagas MUST
 * use isUpdateApiFailure below instead: their success bodies can carry a domain
 * top-level `status` (Booking/Plan/BookingRefund/EnrollmentInvitation), which
 * this predicate would misread as failure.
 *
 * The leading `!res` / `instanceof Error` clauses preserve the pre-#600
 * defensive contract still pinned by the preference and searchTopics suites
 * (a fetcher that historically leaked a raw Error, or a falsy resolve, is a
 * failure, never a SUCCESS carrying junk state).
 *
 * @param {*} res an apiService response
 * @returns {boolean} true iff res carries a failure signal
 */
export const isApiFailure = (res) =>
    !res || res instanceof Error || Boolean(res.errStatus || res.status);

/**
 * Failure-detection for UPDATE-FAMILY fetchers ONLY: doUpdateCall, doDeleteCall
 * (POST/PUT/DELETE via the shared helpers).
 *
 * WHY a separate predicate (PR #612 gate finding, the 8 blocking hits): the
 * update/delete fetchers NEVER emit a top-level `status` on failure — their
 * failure signal is exclusively `errStatus` (numeric non-2xx, string "500" on
 * network reject). But their real 200/201 SUCCESS bodies routinely carry a
 * top-level DOMAIN `status` field: Booking.status ("SUCCESS"/"CANCELED"/...),
 * Plan.status ("ACTIVE"), BookingRefund.status (RefundStatus),
 * EnrollmentInvitation.status (InvitationStatus). Running isApiFailure's
 * `status` clause against these sagas classifies GENUINE SUCCESS as failure
 * (e.g. the post-checkout updatePaymentStatus rendering the error path on a
 * successful payment). So here: no `status` clause, ever.
 *
 * Use isApiFailure for doGetCall / inline-GET-shape fetchers (their failure
 * signal IS `status` and their traced success bodies never carry one); use
 * isUpdateApiFailure for anything backed by doUpdateCall/doDeleteCall.
 *
 * @param {*} res an apiService response from an update-family fetcher
 * @returns {boolean} true iff res carries a failure signal
 */
export const isUpdateApiFailure = (res) =>
    !res || res instanceof Error || Boolean(res.errStatus);

/**
 * The failure status code from an apiService response — numeric or string — for
 * the dispatched ERROR action's `status` field (undefined on success or on a raw
 * Error resolve). Mirrors the `errStatus || status` field used since quiz.js:82.
 * Safe for both families: update-family failures only ever carry errStatus.
 *
 * @param {*} res an apiService response
 * @returns {number|string|undefined}
 */
export const apiFailureStatus = (res) => res?.errStatus || res?.status;
